import React from 'react'
import PropTypes from 'prop-types'
import styled, { css } from 'styled-components'

import { BodyCopy, SmallBodyCopy } from 'components/Text'
import { Subtitle1 } from 'ds/Text'

const ArrowsWrapper = styled.span.attrs({ className: 'arrows_wrapper' })`
	position: relative;
	width: 1rem;
	height: 1rem;

	${p => p.nudge && `top: ${p.nudge}px;`}

	& svg {
		vertical-align: middle;
		width: auto;
	}
	${p =>
		p.size === 'small' &&
		p.type !== 'angled' &&
		css`
			& svg {
				height: 14px;
			}
		`}
`

const StraightArrow = styled.span`
	position: absolute;
	top: 0%;
	left: 0%;
`

const StraightArrowAux = styled.span`
	position: absolute;
	top: 0%;
	left: 0%;
	transform: translateX(-1rem);
	pointer-events: none;
	opacity: 0;
`

const AngledArrow = styled.span`
	position: absolute;
	top: 0%;
	left: 0%;
`

const AngledArrowAux = styled.span`
	position: absolute;
	top: 0%;
	left: 0%;
	transform: translateX(-0.5rem) translateY(0.5rem);
	pointer-events: none;
	opacity: 0;
`

const Wrapper = styled.div`
	display: ${p => p.display};
	padding-right: 1rem;
	color: ${p => p.theme.colors.light[p?.color] || p.color || 'currentColor'};
	${p =>
		p.display === 'flex' &&
		`
			display: flex;
			align-items: center;

			${
				p.noGrow
					? `
			width: fit-content;
			justify-content: flex-start;`
					: `
			width: 100%;
			justify-content: space-between;
			`
			}
		`}
	&:hover ${AngledArrow} {
		transform: translateX(0.5rem) translateY(-0.5rem);
		opacity: 0;
		transition:
			transform 0.45s var(--ease-quart-in-out),
			opacity 0.25s 0.1s;
	}
	&:hover ${AngledArrowAux} {
		transform: none;
		opacity: 1;
		transition: all 0.45s var(--ease-quart-in-out) 0.1s;
	}
	&:hover ${StraightArrow} {
		transform: translateX(2rem);
		opacity: 0;
		transition:
			transform 0.45s var(--ease-quart-in-out),
			opacity 0.25s;
	}
	&:hover ${StraightArrowAux} {
		transform: none;
		opacity: 1;
		transition: all 0.45s var(--ease-quart-in-out);
	}
`

const TextWrapper = styled.span`
	display: inline-block;
	margin-right: 9px;
`

const AngledSVG = () => {
	return (
		<svg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'>
			<path d='M3.53552 11.5352L10.6066 4.46409' stroke='currentColor' />
			<path d='M4.25537 3.74414C6.1915 4.16504 10.3163 4.75429 11.3264 3.74414' stroke='currentColor' />
			<path d='M11.5792 11.0684C11.1583 9.13223 10.5691 5.00744 11.5792 3.99729' stroke='currentColor' />
		</svg>
	)
}

export const StraightSVG = () => {
	return (
		<svg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'>
			<path d='M2 8L12 8' stroke='currentColor' />
			<path d='M8.01807 3C9.0895 4.66667 11.5895 8 13.0181 8' stroke='currentColor' />
			<path d='M8.01807 13.3574C9.0895 11.6908 11.5895 8.35742 13.0181 8.35742' stroke='currentColor' />
		</svg>
	)
}

export const Arrow = ({ size, type, nudge }) => {
	if (type === 'angled') {
		return (
			<ArrowsWrapper size={size} type={type} nudge={nudge}>
				<AngledArrow>
					<AngledSVG />
				</AngledArrow>
				<AngledArrowAux>
					<AngledSVG />
				</AngledArrowAux>
			</ArrowsWrapper>
		)
	}
	return (
		<ArrowsWrapper size={size} type={type} nudge={nudge}>
			<StraightArrow>
				<StraightSVG />
			</StraightArrow>
			<StraightArrowAux>
				<StraightSVG />
			</StraightArrowAux>
		</ArrowsWrapper>
	)
}

Arrow.propTypes = {
	display: PropTypes.oneOf(['inline-block', 'flex']),
	type: PropTypes.oneOf(['straight', 'angled']),
	size: PropTypes.oneOf(['regular', 'small', 'subtitle1']),
	nudge: PropTypes.number,
}

const ArrowText = ({
	as,
	display = 'inline-block',
	type = 'straight',
	color,
	size = 'regular',
	nudge,
	children,
	...props
}) => {
	return (
		<Wrapper as={as} color={color} display={display} {...props}>
			<TextWrapper>
				{size === 'small' && <SmallBodyCopy>{children}</SmallBodyCopy>}
				{size === 'regular' && <BodyCopy>{children}</BodyCopy>}
				{size === 'subtitle1' && <Subtitle1>{children}</Subtitle1>}
			</TextWrapper>
			<Arrow type={type} size={size} display={display} nudge={nudge} />
		</Wrapper>
	)
}

ArrowText.propTypes = {
	as: PropTypes.any,
	color: PropTypes.string,
	nudge: PropTypes.number,
	display: PropTypes.oneOf(['inline-block', 'flex']),
	type: PropTypes.oneOf(['straight', 'angled']),
	size: PropTypes.oneOf(['regular', 'small']),
}

export const ArrowWrapper = ({
	as,
	display = 'inline-block',
	type = 'straight',
	color,
	nudge,
	children,
	size,
	...props
}) => {
	return (
		<Wrapper as={as} color={color} display={display} {...props} className='arrow_wrapper'>
			{children}
			<Arrow type={type} display={display} nudge={nudge} size={size} />
		</Wrapper>
	)
}

export default ArrowText
